// Pending cloud-launch stash (#88 / #87).
//
// When a user tries to launch a capsule on Managed Cloud but is paywalled (402
// cloud_subscription_required), we remember WHAT they were trying to launch in
// localStorage before sending them to Plan & billing to subscribe. After
// checkout returns and the entitlement lands, SettingsBillingReturn reads this
// stash and offers to RESUME the original launch (POST /v1/launches is
// idempotent on capsule_ref, so a reload or double-click converges to one app).
//
// Best-effort, exactly like GetRun's StoredActiveRun: a throwing/blocked
// localStorage (private mode, quota) must never break the paywall or the resume.

const KEY = "ato_pending_cloud_launch";

/** A stash older than this is considered abandoned (checkout never completed). */
const STALE_AFTER_MS = 60 * 60 * 1000; // 1 hour

export interface PendingCloudLaunch {
  /** Scoped capsule ref the user was trying to launch ("publisher/slug"). */
  capsuleRef: string;
  /** Which surface attempted the launch — "open" (/open) or "run" (Run screen). */
  source: "open" | "run";
  /** Launch intent; defaults to "open". */
  intent: string;
  /** Epoch ms when stashed (for staleness). */
  ts: number;
}

/** Persist the capsule the user was trying to launch before subscribing. */
export function savePendingCloudLaunch(
  pending: Omit<PendingCloudLaunch, "ts"> & { ts?: number },
): void {
  try {
    const value: PendingCloudLaunch = {
      capsuleRef: pending.capsuleRef,
      source: pending.source,
      intent: pending.intent || "open",
      ts: pending.ts ?? Date.now(),
    };
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // localStorage unavailable (private mode / quota) → resume just won't offer.
  }
}

/** Read the stashed launch, or null when absent / stale / unparseable. */
export function loadPendingCloudLaunch(): PendingCloudLaunch | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingCloudLaunch>;
    if (typeof parsed?.capsuleRef !== "string" || !parsed.capsuleRef) {
      return null;
    }
    const ts = typeof parsed.ts === "number" ? parsed.ts : 0;
    if (!ts || Date.now() - ts > STALE_AFTER_MS) {
      clearPendingCloudLaunch();
      return null;
    }
    return {
      capsuleRef: parsed.capsuleRef,
      source: parsed.source === "run" ? "run" : "open",
      intent: typeof parsed.intent === "string" ? parsed.intent : "open",
      ts,
    };
  } catch {
    return null;
  }
}

/**
 * Reconstruct a pending launch from checkout-return URL params (#139): the
 * ato-api success_url echoes `capsule_ref` / `intent`, so resume still works when
 * the localStorage stash is gone (different tab/device, cleared, or expired). The
 * hash defaults to the live one; pass it explicitly in tests.
 */
export function pendingCloudLaunchFromParams(
  hash: string = typeof window !== "undefined" ? window.location.hash : "",
): PendingCloudLaunch | null {
  try {
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const capsuleRef = params.get("capsule_ref");
    if (!capsuleRef) return null;
    return {
      capsuleRef,
      source: "open",
      intent: params.get("intent") || "open",
      ts: Date.now(),
    };
  } catch {
    return null;
  }
}

/** Drop the stash — on resume success, or an explicit cancel. */
export function clearPendingCloudLaunch(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore — a stale stash self-expires via STALE_AFTER_MS on the next read.
  }
}
